import type { DmScanDetail, DmScanList, DmScanPhoto, Morphing } from './types';

export interface DmScansApi {
    get: <T>(url: string) => Promise<{ data: T }>;
}

export interface DmScansServiceConfig {
    api: DmScansApi;
    /** Prefixed onto relative proxy URLs so dev cross-origin photos resolve. */
    apiBaseUrl: string;
    /** Wraps transport errors into an app-specific error type. */
    mapError?: (err: unknown) => unknown;
}

// The dashboard's axios interceptor already unwraps `{success,data}`, the
// files-viewer's does not — tolerating both keeps one service for either host.
function unwrap<T>(body: unknown): T {
    if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
        return (body as { data: T }).data;
    }
    return body as T;
}

// Every scans consumer spreads or `.find`s `list.scans`, and the
// `scans.length === 0` guards they lean on read `undefined` on a non-array —
// so a payload that ships `scans` as an object sails past the guard and
// throws "not iterable" straight into the patient page's ErrorBoundary.
// Normalise once here so a malformed list degrades to "no scans" instead of a
// white screen, and the callers stay simple.
function normalizeScanList(list: DmScanList | null): DmScanList | null {
    if (!list) return null;
    if (Array.isArray(list.scans)) return list;
    return { ...list, scans: [] };
}

export interface DmScansService {
    fetchDmScans: (patientId: string) => Promise<DmScanList | null>;
    fetchDmScanDetail: (patientId: string, scanId: number) => Promise<DmScanDetail | null>;
    fetchMorphing: (patientId: string) => Promise<Morphing>;
}

export function createDmScansService({
    api,
    apiBaseUrl,
    mapError
}: DmScansServiceConfig): DmScansService {
    const absolutify = (url: string | null): string | null => {
        if (!url) return null;
        if (/^https?:\/\//i.test(url)) return url;
        return `${apiBaseUrl}${url}`;
    };

    const absolutifyPhoto = (photo: DmScanPhoto): DmScanPhoto => ({
        ...photo,
        previewUrl: absolutify(photo.previewUrl),
        originalUrl: absolutify(photo.originalUrl)
    });

    const run = async <T>(fn: () => Promise<T>): Promise<T> => {
        if (!mapError) return fn();
        try {
            return await fn();
        } catch (err) {
            throw mapError(err);
        }
    };

    return {
        fetchDmScans: (patientId) =>
            run(async () => {
                const res = await api.get<unknown>(`/patients/${patientId}/monitoring/scans`);
                return normalizeScanList(unwrap<DmScanList | null>(res.data));
            }),

        fetchDmScanDetail: (patientId, scanId) =>
            run(async () => {
                const res = await api.get<unknown>(
                    `/patients/${patientId}/monitoring/scans/${scanId}`
                );
                const detail = unwrap<DmScanDetail | null>(res.data);
                if (!detail) return null;
                return { ...detail, photos: (detail.photos ?? []).map(absolutifyPhoto) };
            }),

        fetchMorphing: (patientId) =>
            run(async () => {
                const res = await api.get<unknown>(`/patients/${patientId}/monitoring/morphing`);
                const morphing = unwrap<Morphing>(res.data);
                return { ...morphing, url: absolutify(morphing?.url ?? null) };
            })
    };
}
